<template>
  <!--
    @mousedown.stop is needed for Popover.vue.
    Datepicker changes the element on mousedown and Popover sees that event.target isn't a descendant anymore.
    It'd work on @click but Popover needs to use mousedown.
  -->
  <div
    class="date-popup block sm:flex justify-between"
    @mousedown.stop
  >
    <div ref="datepicker" />
    <div class="date-popup__time sm:w-1/2">
      <div
        class="date-popup__time--wrapper text-center font-semibold"
        :class="{'text-grey-600': !isDateSelected}"
      >
        <div
          v-if="label"
          class="p-4 pb-0"
        >
          {{ label }}
        </div>
      </div>

      <TimeRange
        :time-format="$store.state.l10n.timeFormat"
        class="mt-5 sm:mt-3 flex justify-between sm:p-4 sm:flex-col"
        :input-classes="[
          'w-1/2 sm:w-full mr-2 sm:mr-0',
          'w-1/2 sm:w-full ml-2 sm:ml-0 sm:mt-4'
        ]"
        :model-value="[form.timeFrom, form.timeTo]"
        data-test-id-from="search-page__time-from"
        data-test-id-to="search-page__time-to"
        :max-value="timeSlotsMaxValue"
        @update:modelValue="change"
      />
    </div>
  </div>
</template>

<script>
import TimeRange from '@/components/Ui/Forms/TimeRange.vue';
import { timeSlotsMaxValue } from '@/services/date';
import { FORMAT_WEEKDAY_FULL_DAY_MONTH_FULL_YEAR, formatDate } from '@/services/dateFormatter.js';

export default {
  name: 'DatePopup',
  components: { TimeRange },

  props: {
    form: {
      type: Object,
      default: () => ({
        timeFrom: null,
        timeTo: null,
        date: null,
      }),
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      pikaday: null,
      keyDownOverrideListener: null,
      timeSlotsMaxValue,
    };
  },

  computed: {
    label() {
      if (this.isDateSelected) {
        const date = window.dayjs(this.form.date, 'DD/MM/YYYY').toDate();
        return formatDate(date, this.$store.state.l10n.ISOCountryCode, FORMAT_WEEKDAY_FULL_DAY_MONTH_FULL_YEAR);
      }
      if (!this.isMobile) {
        return 'Select date and time';
      }
      return '';
    },
    isDateSelected() {
      return !!this.form.date;
    },
  },

  watch: {
    'form.date': function (value) { // eslint-disable-line func-names
      if (!value) {
        this.pikaday.clear();
      }
    },
  },

  methods: {
    change(value) {
      if (value[0] !== this.form.timeFrom) {
        this.$emit('change', 'timeFrom', value[0]);
      }
      if (value[1] !== this.form.timeTo) {
        this.$emit('change', 'timeTo', value[1]);
      }
    },
  },

  mounted() {
    this.pikaday = App.es6.shared.pikaday({
      weekStart: this.$store.state.l10n.weekStart,
      uiDateFormat: this.$store.state.l10n.datepickerFormat,
      element: this.$refs.datepicker,
      bound: false,
      hideClearDateButton: true,
      onSelect: (value) => this.$emit('change', 'date', value),
      defaultDate: this.form.date,
      showDaysInNextAndPreviousMonths: false,
      keyboardInput: true,
    });

    this.keyDownOverrideListener = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', this.keyDownOverrideListener);
  },
  unmounted() {
    this.pikaday.destroy();
    document.removeEventListener('keydown', this.keyDownOverrideListener);
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;
@use "@sass/base/variables";

$border: 1px solid ds-variables.$grey-300;
$title-height: 52px;

.date-popup {
  :deep(.pika-single) {
    box-shadow: none;
    border: none;
  }

  :deep(.pika-lendar) {
    width: 100%;
  }

  :deep(.pika-prev:not(.is-disabled):hover),
  :deep(.pika-next:not(.is-disabled):hover) {
    background-color: ds-variables.$grey-200;
  }

  :deep(.pika-prev),
  :deep(.pika-next) {
    background-color: ds-variables.$white;
  }

  :deep(.pika-title) {
    margin-bottom: 16px;
  }

  :deep(.pika-label) {
    font-size: 15px;
    padding: 3px;
    font-weight: 600;
  }

  :deep(.pika-button) {
    width: 100%;
  }

  @media only screen and (min-width: variables.$screen-sm-min) {
    :deep(.pika-single) {
      padding: 0;
      margin: 0;
      width: 50%;
    }

    :deep(.pika-title) {
      padding: 0.75rem 1rem 1rem;
      border-bottom: $border;
      height: $title-height;
    }

    :deep(.pika-lendar) {
      margin: 0;
    }

    :deep(.pika-table) {
      padding: 10px;
      border-collapse: separate;
    }

    .date-popup__time {
      border-left: $border;
    }
  }
}

.date-popup__time--wrapper {
  height: $title-height;
  border-bottom: $border;

  @media #{variables.$for-mobile} {
    border-top: $border;
    border-bottom: none;
    padding-bottom: 0;
    height: auto;
    color: ds-variables.$grey-800;
  }
}
</style>
